import { useEffect, useState } from "react";
import { useSettings } from "@/lib/useSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save } from "lucide-react";

const FIELDS = [
  ["company", "Vereinsname (rechts im Briefkopf)"],
  ["sender_line", "Absenderzeile über der Anschrift"],
  ["street", "Straße"],
  ["zip_city", "PLZ / Ort"],
  ["tax_no", "Steuernummer"],
  ["signature", "Unterschrift unter „Herzliche Grüße“"],
];

function Banks({ banks, onChange }) {
  const set = (i, k, v) => onChange(banks.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <div className="space-y-2">
      <Label>Bankverbindungen (Fußzeile)</Label>
      {banks.map((b, i) => (
        <div key={i} className="grid sm:grid-cols-3 gap-2">
          {["name", "iban", "bic"].map((k) => (
            <Input key={k} value={b[k] || ""} onChange={(e) => set(i, k, e.target.value)} placeholder={k === "name" ? "Bank" : k.toUpperCase()} data-testid={`acc-bank-${i}-${k}`} />
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...banks, { name: "", iban: "", bic: "" }])} data-testid="acc-bank-add">Bank hinzufügen</Button>
    </div>
  );
}

function Dunning({ dun, onSave }) {
  const STAGES = ["1. Mahnung (Zahlungserinnerung)", "2. Mahnung", "3. / letzte Mahnung"];
  const set = (i, k, v) => onSave(dun.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2" data-testid="dunning-settings">
      <Label>Mahnstufen</Label>
      <p className="text-xs text-slate-500">Es wird nichts automatisch verschickt – in der Buchhaltung erscheint nur ein Hinweis, sobald eine Stufe fällig ist.</p>
      {STAGES.map((l, i) => (
        <div key={l} className="grid sm:grid-cols-[200px_1fr_1fr] gap-2 items-center text-sm">
          <span className="font-medium text-slate-700">{l}</span>
          <label className="flex items-center gap-2">Hinweis nach <Input inputMode="numeric" value={dun[i].after_days} onChange={(e) => set(i, "after_days", Number(e.target.value.replace(/\D/g, "")))} className="w-20" data-testid={`dunning-${i}-after`} /> Tagen {i === 0 ? "ab Rechnung" : "ab vorheriger Mahnung"}</label>
          <label className="flex items-center gap-2">Zahlungsfrist <Input inputMode="numeric" value={dun[i].deadline_days} onChange={(e) => set(i, "deadline_days", Number(e.target.value.replace(/\D/g, "")))} className="w-20" data-testid={`dunning-${i}-deadline`} /> Tage</label>
        </div>
      ))}
    </div>
  );
}

export default function AccountingSettings() {
  const { data, save } = useSettings();
  const [a, setA] = useState(null);
  const [dun, setDun] = useState(null);
  useEffect(() => { if (data) { setA(data.accounting); setDun(data.dunning); } }, [data]);
  if (!a) return <div className="text-slate-400">Lädt…</div>;
  const banks = a.banks.filter((b) => b.name || b.iban);
  return (
    <div className="max-w-3xl">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="accounting-settings">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-800">Buchhaltung – Briefkopf</h2>
          <p className="text-sm text-slate-500">Diese Angaben erscheinen auf Auftragsbestätigungen (und später Rechnungen und Mahnungen).</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Input value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} className="mt-1" data-testid={`acc-${k}`} /></div>
          ))}
        </div>
        <div>
          <Label>Kontaktblock rechts (je Zeile „Bezeichnung|Wert“)</Label>
          <Textarea value={a.contact || ""} onChange={(e) => setA({ ...a, contact: e.target.value })} rows={9} className="mt-1 font-mono text-xs" data-testid="acc-contact" />
        </div>
        <Banks banks={a.banks} onChange={(b) => setA({ ...a, banks: b })} />
        <Dunning dun={dun} onSave={setDun} />
        <Button onClick={() => save({ accounting: { ...a, banks }, dunning: dun })} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="acc-save"><Save size={16} className="mr-1.5" /> Speichern</Button>
      </div>
    </div>
  );
}
